import { Clock, Hourglass, Sparkles } from 'lucide-react'
import { useProModalStore } from '../../stores/proModalStore'
import { useCountdownToMidnightUtc } from '../../hooks/useCountdownToMidnightUtc'
import { useLocale } from '../../i18n/context'

export interface RateLimitInfo {
  limit: number
  used: number
}

interface Props {
  isPro: boolean
  /** `{limit, used}` from the 429 body when the backend provides it. */
  rateLimitInfo?: RateLimitInfo
  /**
   * Verbatim message from the backend 429 body (e.g. "Daily AI explain limit
   * reached (10 calls/day). Upgrade to Pro for higher limits."). The frontend
   * surfaces this as the title so any future backend copy change does not
   * require a frontend release.
   */
  backendMessage?: string
  /**
   * `compact` is the WordReferencePanel AI-tab variant — smaller fonts and
   * tighter padding so the state fits inside the tab. Default is the modal
   * variant.
   */
  compact?: boolean
}

export default function AiRateLimitedState({
  isPro,
  rateLimitInfo,
  backendMessage,
  compact = false,
}: Props) {
  const { hours, minutes } = useCountdownToMidnightUtc(true)
  const openProModal = useProModalStore((s) => s.open)
  const { tr } = useLocale()
  const rl = tr.aiRateLimit

  const titleSize = compact ? 15 : 17
  const bodySize = compact ? 13 : 14
  const iconSize = compact ? 22 : 28
  const iconWell = compact ? 40 : 52
  const gap = compact ? 10 : 14
  const pad = compact ? '12px 0 4px' : '20px 0 4px'

  // Use the first sentence of the backend message as the title (the backend
  // emits a single trailing-period sentence followed by an upsell hint). Fall
  // back to a generic copy if the backend didn't provide one.
  const title = sentence(backendMessage) ?? rl.dailyLimitReached

  const usageLine = rateLimitInfo
    ? rl.usageLine(rateLimitInfo.used, rateLimitInfo.limit)
    : null

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap,
        padding: pad,
        textAlign: 'center',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <div
        aria-hidden
        style={{
          width: iconWell,
          height: iconWell,
          borderRadius: '50%',
          background: 'var(--accent-soft)',
          color: 'var(--accent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Clock size={iconSize} strokeWidth={1.75} />
      </div>

      <p
        style={{
          margin: 0,
          fontSize: titleSize,
          fontWeight: 700,
          color: 'var(--text-primary)',
          maxWidth: 320,
        }}
      >
        {title}
      </p>

      {usageLine && (
        <p style={{ margin: 0, fontSize: bodySize, color: 'var(--text-secondary)', maxWidth: 320 }}>
          {usageLine}
        </p>
      )}

      <ResetTimer hours={hours} minutes={minutes} size={bodySize} />

      {!isPro ? (
        <button
          type="button"
          onClick={openProModal}
          aria-label={rl.upgradeLabel}
          style={{
            marginTop: 4,
            padding: '10px 22px',
            borderRadius: 50,
            border: 'none',
            background: 'linear-gradient(135deg, #F59E0B, #D97706)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            minHeight: 44,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)',
          }}
        >
          <Sparkles size={16} />
          {rl.upgradeLabel}
        </button>
      ) : (
        <p
          style={{
            margin: '2px 0 0',
            fontSize: bodySize - 1,
            color: 'var(--text-secondary)',
            maxWidth: 320,
          }}
        >
          {rl.proQuotaUsed}
        </p>
      )}
    </div>
  )
}

function ResetTimer({ hours, minutes, size }: { hours: number; minutes: number; size: number }) {
  const { tr } = useLocale()
  const rl = tr.aiRateLimit
  let text: string
  if (hours === 0 && minutes === 0) text = rl.resetsInLess
  else if (hours === 0) text = rl.resetsInMins(minutes)
  else if (minutes === 0) text = rl.resetsInHours(hours)
  else text = rl.resetsInHoursAndMins(hours, minutes)
  return (
    <p
      style={{
        margin: 0,
        fontSize: size,
        color: 'var(--text-secondary)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <Hourglass size={size} strokeWidth={1.75} aria-hidden style={{ opacity: 0.7 }} />
      <span>{text}</span>
    </p>
  )
}

function sentence(msg?: string): string | undefined {
  if (!msg) return undefined
  const trimmed = msg.trim()
  if (!trimmed) return undefined
  const firstStop = trimmed.indexOf('.')
  return firstStop === -1 ? trimmed : trimmed.slice(0, firstStop + 1)
}
